import React from 'react';
import FeedbackStatusBadge from './FeedbackStatusBadge';
import CommentThread from './CommentThread';
import CommentReplyForm from './CommentReplyForm';

/**
 * ThreadFeedbackSection - Generisk container för tråd-baserad feedback
 * Designad för SINGEL KONVERSATIONER som i Offert-flödet
 * (Inte per-rad, utan en gemensam tråd)
 */
const ThreadFeedbackSection = ({
  title = 'Feedback',
  // Vad feedbacken gäller, i böjd form: "offerten". Raden inuti sektionen skrev
  // tidigare "Feedback på {title}", men samtliga anrop skickar in en title som
  // redan innehåller ordet feedback — det blev "Feedback på kundens feedback"
  // i kalkylen och "Feedback på feedback" i offerten. Utan amne skrivs bara
  // title ut, så en ny anropare inte kan återskapa dubbleringen.
  amne = null,
  feedbackThread,           // Array av meddelanden { by, message, at, userId }
  feedbackHistory = [],     // Array av tidigare versioners feedback från backend
  currentVersion,           // Nuvarande version (t.ex. "1.0") - för att dölja historik på version 1.0
  expandedFeedbackItems,
  setExpandedFeedbackItems,
  newReplies,               // { [key]: string }
  savedReplies,             // { [key]: string }
  onReplyChange,            // (key, value) => void
  onReplySave,              // (key) => void
  onMarkAsOK,               // () => void
  onSendAllReplies,         // () => void
  sendingResponse,
  procurement,              // Procurement-data för namn-fallback
  user,                     // Current user för namn-fallback
  viewMode = 'consultant',  // 'consultant' eller 'customer'
  feedbackKey = 'offert_feedback', // Unik nyckel för denna feedback-tråd
  onProofread               // () => void — visar Korrekturläs-knapp om angivet
}) => {
  // Bestäm status baserat på SENASTE meddelandet
  const messages = feedbackThread?.messages || [];
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isLastMessageMarkedAsOK = lastMessage?.message === '✓ Markerad som OK';
  
  let feedbackStatus = 'Svarat'; // Default
  let anyThreadNeedsReply = false;

  if (lastMessage) {
    if (isLastMessageMarkedAsOK) {
      // Senaste meddelandet är "Markerad som OK" → "Feedback OK" för båda
      feedbackStatus = 'Feedback OK';
      anyThreadNeedsReply = false;
    } else if (viewMode === 'consultant') {
      // Konsult-vy: Kolla vem som skrev senast
      if (lastMessage.by === 'customer') {
        feedbackStatus = 'Ej svarat';
        anyThreadNeedsReply = true;
      } else {
        feedbackStatus = 'Svarat';
        anyThreadNeedsReply = false;
      }
    } else {
      // Kund-vy: Kolla vem som skrev senast
      if (lastMessage.by === 'consultant') {
        feedbackStatus = 'Ej svarat';
        anyThreadNeedsReply = true;
      } else {
        feedbackStatus = 'Svarat';
        anyThreadNeedsReply = false;
      }
    }
  }

  // Om ingen feedback finns i nuvarande version OCH ingen historik, visa ingenting
  const hasCurrentFeedback = feedbackThread && feedbackThread.messages && feedbackThread.messages.length > 0;
  const hasHistoricalFeedback = feedbackHistory && feedbackHistory.length > 0;
  
  if (!hasCurrentFeedback && !hasHistoricalFeedback) {
    return null;
  }

  // Bygg meddelande-lista med korrekt författar-information
  let threadMessages = [];

  if (messages.length > 0) {
    messages.forEach(m => {
      // Använd användardata från backend om tillgängligt
      let authorName;
      if (m.userId && m.userId.firstName && m.userId.lastName) {
        authorName = `${m.userId.firstName} ${m.userId.lastName}`;
      } else {
        // Fallback baserat på 'by' field och current user
        if (m.by === 'consultant') {
          authorName = (user?.firstName && user?.lastName) ?
            `${user.firstName} ${user.lastName}` : 'Byggkonsult';
        } else {
          // För kund-meddelanden, använd procurement-data
          const customerInfo = procurement?.customerInfo;
          if (customerInfo?.firstName && customerInfo?.lastName) {
            authorName = `${customerInfo.firstName} ${customerInfo.lastName}`;
          } else {
            authorName = 'Kund';
          }
        }
      }

      threadMessages.push({
        by: m.by,
        text: m.message,
        at: m.at,
        userId: m.userId,
        author: { name: authorName }
      });
    });
  }

  // Sortera kronologiskt
  threadMessages.sort((a, b) => {
    const ta = a?.at ? new Date(a.at).getTime() : 0;
    const tb = b?.at ? new Date(b.at).getTime() : 0;
    return ta - tb;
  });

  // Bestäm om sektionen ska vara öppen
  // Om användaren har klickat (expandedFeedbackItems har värde), använd det
  // Annars, öppna bara om konsulten behöver svara
  const isOpen = expandedFeedbackItems.hasOwnProperty(feedbackKey) 
    ? expandedFeedbackItems[feedbackKey] 
    : anyThreadNeedsReply;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <details
        className="group"
        open={isOpen}
      >
        <summary className="px-4 py-3 bg-leadme-pink-dark text-white rounded-t-lg cursor-pointer flex items-center justify-between">
          <h2 className="text-base font-medium">{title}</h2>
          <div className="flex items-center gap-2">
            <FeedbackStatusBadge anyNeedsReply={anyThreadNeedsReply} statusText={feedbackStatus} />
            <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </summary>

        <div className="p-4">
          {/* Tidigare versioners feedback - visa endast om version > 1.0 */}
          {hasHistoricalFeedback && currentVersion !== '1.0' && (
            <div className="mb-4">
              <details className="group/history">
                <summary className="cursor-pointer select-none px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-medium text-amber-900">
                        Visa tidigare versioners feedback ({feedbackHistory.length} {feedbackHistory.length === 1 ? 'version' : 'versioner'})
                      </span>
                    </div>
                    <svg className="w-4 h-4 text-amber-600 transition-transform group-open/history:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </summary>
                
                <div className="mt-3 space-y-3">
                  {feedbackHistory.map((versionFeedback, idx) => {
                    // Bygg meddelande-lista för denna version
                    const versionMessages = versionFeedback.messages.map(m => {
                      let authorName;
                      if (m.userId && m.userId.firstName && m.userId.lastName) {
                        authorName = `${m.userId.firstName} ${m.userId.lastName}`;
                      } else {
                        if (m.by === 'consultant') {
                          authorName = (user?.firstName && user?.lastName) ?
                            `${user.firstName} ${user.lastName}` : 'Byggkonsult';
                        } else {
                          const customerInfo = procurement?.customerInfo;
                          if (customerInfo?.firstName && customerInfo?.lastName) {
                            authorName = `${customerInfo.firstName} ${customerInfo.lastName}`;
                          } else {
                            authorName = 'Kund';
                          }
                        }
                      }
                      return {
                        by: m.by,
                        text: m.message,
                        at: m.at,
                        userId: m.userId,
                        author: { name: authorName }
                      };
                    });
                    
                    versionMessages.sort((a, b) => {
                      const ta = a?.at ? new Date(a.at).getTime() : 0;
                      const tb = b?.at ? new Date(b.at).getTime() : 0;
                      return ta - tb;
                    });

                    return (
                      <div key={versionFeedback.offertId} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                        {/* Version header */}
                        <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-700">
                              📦 Version {versionFeedback.version}
                            </span>
                            {versionFeedback.isObsolete && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                                Ersatt
                              </span>
                            )}
                            {versionFeedback.isCurrentVersion && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                Nuvarande
                              </span>
                            )}
                          </div>
                          {versionFeedback.sentAt && (
                            <span className="text-xs text-gray-500">
                              {new Date(versionFeedback.sentAt).toLocaleDateString('sv-SE', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </span>
                          )}
                        </div>
                        {/* Messages */}
                        <div className="px-3 py-2 bg-white">
                          <CommentThread messages={versionMessages} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            </div>
          )}

          {/* Nuvarande version feedback - visa bara om det finns meddelanden */}
          {hasCurrentFeedback && (
            <div className="space-y-3">
              <div className="border border-blue-200 rounded-lg overflow-hidden">
              <div
                className="flex items-center justify-between p-3 cursor-pointer select-none bg-blue-50"
                role="button"
                tabIndex={0}
                aria-expanded={expandedFeedbackItems[feedbackKey] !== false && !isLastMessageMarkedAsOK}
                onClick={() => setExpandedFeedbackItems(prev => ({
                  ...prev,
                  [feedbackKey]: prev[feedbackKey] === false ? true : false
                }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedFeedbackItems(prev => ({
                      ...prev,
                      [feedbackKey]: prev[feedbackKey] === false ? true : false
                    }));
                  }
                }}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{amne ? `Feedback på ${amne}` : title}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                    <span className={`w-2 h-2 rounded-full ${
                      feedbackStatus === 'Ej svarat' ? 'bg-red-500' : 
                      feedbackStatus === 'Feedback OK' ? 'bg-blue-500' : 
                      'bg-green-500'
                    }`}></span>
                    {feedbackStatus}
                  </span>
                  <svg className={`w-4 h-4 text-gray-600 transition-transform ${expandedFeedbackItems[feedbackKey] !== false ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {expandedFeedbackItems[feedbackKey] !== false && (
                <div className="px-3 pb-3 bg-white">
                  <CommentThread messages={threadMessages} />

                  {/* Svarsfält - visa bara om SENASTE meddelandet inte är "Markerad som OK" */}
                  {!isLastMessageMarkedAsOK && (
                    <>
                      {/* Visa sparat-men-ej-skickat svar tydligt så BK ser sin egen text */}
                      {savedReplies[feedbackKey] && !newReplies[feedbackKey] && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-green-700 mb-1 flex items-center gap-1">
                                ✓ Sparat svar (ej skickat än)
                              </div>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                                {savedReplies[feedbackKey]}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => onReplyChange(feedbackKey, savedReplies[feedbackKey])}
                              className="flex-shrink-0 px-2 py-1 text-xs font-medium text-green-700 bg-white border border-green-300 rounded hover:bg-green-50"
                              title="Flytta tillbaka till textrutan för att redigera"
                            >
                              Redigera
                            </button>
                          </div>
                        </div>
                      )}
                      <CommentReplyForm
                        commentKey={feedbackKey}
                        value={newReplies[feedbackKey] || ''}
                        onChange={(value) => onReplyChange(feedbackKey, value)}
                        onSave={() => onReplySave(feedbackKey)}
                        hasUnsavedChanges={Boolean(newReplies[feedbackKey]?.trim())}
                        viewMode={viewMode}
                      />

                      {/* Markera som OK knapp - visa för båda parter */}
                      <div className="mt-2">
                        <button
                          onClick={onMarkAsOK}
                          disabled={sendingResponse}
                          className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          ✓ Markera som OK
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            </div>
          )}
        </div>

        {/* Skicka alla sparade svar */}
        {hasCurrentFeedback && Object.keys(savedReplies).length > 0 && (
          <div className="px-4 pb-4">
            <div className="flex justify-end gap-2">
              {onProofread && (
                <button
                  type="button"
                  onClick={onProofread}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                  title="Korrekturläs ditt svar innan det skickas"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Korrekturläs
                </button>
              )}
              <button
                type="button"
                onClick={onSendAllReplies}
                disabled={sendingResponse}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                {sendingResponse ? 'Skickar...' : 'Skicka'}
              </button>
            </div>
          </div>
        )}
      </details>
    </div>
  );
};

export default ThreadFeedbackSection;

